import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { RootState } from '..'
import { uploadInputZones } from '../../components/UploadDropzone/types'
import materialsService from '../../services/materialsService'
import actions from './actions'
import { IMaterialState, IPOSTMaterial, ITestFiles } from './types'

import { error } from '../../utils/alertUtils';

const initialState: IMaterialState = {
  files: [],
  uploadingType: undefined,
}

export const addAdditionalMaterial = createAsyncThunk(
  actions.POST_ADDITIONAL_MATERIAL,
  async (payload: { data: IPOSTMaterial[] }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState() as RootState
      const promises = payload.data.map(item => {
        const formData = new FormData()
        formData.append('title', item.title as string)
        formData.append('description', item.description)
        formData.append('report', state.reports.openedCase?.id as unknown as string)
        formData.append('type', item.type)
        formData.append('variant', 'MATERIAL')

        if (item.link) {
          formData.append('link', item.link)
        }

        if (item.type === uploadInputZones.TEXT_FIELD)
          return materialsService.postAdditionalMaterial(formData)

        if (item.type === uploadInputZones.LINK)
          return materialsService.postAdditionalMaterial(formData)

        item.attachments.forEach((file: File, index) => {
          formData.append(`attachments[${index}]attachment`, file)
        })
        return materialsService.postAdditionalMaterial(formData)
      })

      Promise.all(promises).then(() => {
        dispatch(getReportAdditionalMaterial({ reportId: state.reports.openedCase?.id as number }))
      })

      return {}
    } catch (err) {
      if (err instanceof Error) {
        error('Something went wrong while adding additional material');
        return rejectWithValue('Something went wrong while adding additional material')
      }
    }
  },
)

export const getReportAdditionalMaterial = createAsyncThunk(
  actions.GET_ADDITIONAL_MATERIAL,
  async (payload: { reportId: number }, { rejectWithValue }) => {
    try {
      const { data } = await materialsService.getReportAdditionalMaterial(payload.reportId)
      const formatedData = data
        .map(item => {
          if (item?.files.length > 0) {
            return {
              title: item.title,
              description: item?.description,
              type: item.type,
              attachments: item?.files,
              id: item?.id,
            }
          }

          const newFile = {
            title: item.title,
            description: item?.description,
            type: item.type,
            ...(item.type === uploadInputZones.LINK ? { link: item?.link } : {}),
          }
          return newFile
        })
        .flat()
      return formatedData as unknown as ITestFiles[]
    } catch (err) {
      if (err instanceof Error) {
        error('Something went wrong while getting additional material');
        return rejectWithValue('Something went wrong while getting additional material')
      }
    }
  },
)

export const patchMaterial = createAsyncThunk(
  actions.PATCH_MATERIAL,
  async (payload: { material: any; fileId: number }, { rejectWithValue }) => {
    try {
      const { data } = await materialsService.patchAdditionalMaterial(
        payload.fileId,
        payload.material,
      )
      return { data, fileId: payload.fileId }
    } catch (err) {
      if (err instanceof Error) {
        error('Something went wrong while patching additional material')
        return rejectWithValue('Something went wrong while patching additional material')
      }
    }
  },
)

export const patchMaterialFile = createAsyncThunk(
  'materials/patch/files',
  async (payload: { materialId: number; fileId: number[] }, { rejectWithValue }) => {
    try {
      const { data } = await materialsService.patchMaterialFiles(payload.materialId, payload.fileId)
      return { data, fileId: payload.fileId }
    } catch (err) {
      if (err instanceof Error) {
        error('Something went wrong while patching additional material')
        return rejectWithValue('Something went wrong while patching additional material')
      }
    }
  },
)

export const deleteMaterial = createAsyncThunk(
  actions.PATCH_MATERIAL,
  async (payload: { fileId: number }, { rejectWithValue }) => {
    try {
      const { data } = await materialsService.deleteAdditionalMaterial(payload.fileId)
      return { data, fileId: payload.fileId }
    } catch (err) {
      if (err instanceof Error) {
        error('Something went wrong while patching additional material')
        return rejectWithValue('Something went wrong while patching additional material')
      }
    }
  },
)

const materialSlice = createSlice({
  name: 'materials',
  initialState,
  reducers: {
    setUploadingType: (state, action) => {
      state.uploadingType = action.payload.value
    },
  },
  extraReducers: builder => {
    builder.addCase(getReportAdditionalMaterial.fulfilled, (state, { payload }) => {
      state.additionalFiles = payload
    }),
      builder.addCase(patchMaterial.fulfilled, (state, { payload }) => {
        const flatFiles = state.additionalFiles
          ?.map(material => {
            if (material?.attachments) {
              const findItem = material?.attachments?.find(
                attachment => attachment?.id === payload?.fileId,
              )
              if (findItem) {
                return { ...material, attachments: [...material.attachments, payload?.data] }
              }
            }
            return material
          })
          .flat()

        state.additionalFiles = flatFiles
      })
  },
})

export const { setUploadingType } = materialSlice.actions

export default materialSlice.reducer
